import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";

interface MouserCommerceStatus {
  personalOrderApiKeyConfigured: boolean;
  orgOrderApiKeyConfigured: boolean;
  effectiveSource: "personal" | "org" | "none";
}

export function PersonalMouserCommercePanel(): JSX.Element {
  const queryClient = useQueryClient();
  const [draftKey, setDraftKey] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [successNote, setSuccessNote] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const statusQuery = useQuery({
    queryKey: ["personal", "mouser-commerce", "status"],
    queryFn: () => apiFetch<MouserCommerceStatus>("/api/personal/mouser-commerce/status"),
  });

  const saveMutation = useMutation({
    mutationFn: (orderApiKey: string) =>
      apiFetch<{ ok: boolean }>("/api/personal/mouser-commerce/order-api-key", {
        method: "PUT",
        body: { orderApiKey },
      }),
    onSuccess: async () => {
      setDraftKey("");
      setFormError(null);
      setSuccessNote("Saved. Your Mouser cart API calls use this key.");
      window.setTimeout(() => setSuccessNote(null), 5000);
      await queryClient.invalidateQueries({ queryKey: ["personal", "mouser-commerce", "status"] });
    },
    onError: (error: ApiError) => {
      setFormError(error.message);
    },
  });

  const removeMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>("/api/personal/mouser-commerce/order-api-key", {
        method: "DELETE",
      }),
    onSuccess: async () => {
      setFormError(null);
      setSuccessNote("Removed your personal Mouser Order/Cart key.");
      window.setTimeout(() => setSuccessNote(null), 5000);
      await queryClient.invalidateQueries({ queryKey: ["personal", "mouser-commerce", "status"] });
    },
  });

  const testMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ keySource: string; cart: { cartKey: string | null; itemCount: number } | null }>(
        "/api/personal/mouser-commerce/test-cart",
        { method: "POST", body: {} },
      ),
    onSuccess: (data) => {
      setTestMessage(
        `Cart OK (${data.keySource} key). Items in cart: ${data.cart?.itemCount ?? 0}.`,
      );
    },
    onError: (error: ApiError) => {
      setTestMessage(error.message);
    },
  });

  const status = statusQuery.data;

  return (
    <div className="space-y-3 text-sm text-slate-700">
      <p>
        Mouser <strong>Order/Cart</strong> API key from Manage APIs (same UUID as Order and Cart). Your
        cart stays under your Mouser login; other buyers use their own keys. Search/discover still uses
        the org Search key.
      </p>
      {statusQuery.isLoading ? <p className="text-slate-500">Loading…</p> : null}
      {status ? (
        <p className="text-slate-600">
          Personal key:{" "}
          <span className="font-medium text-slate-900">
            {status.personalOrderApiKeyConfigured ? "saved" : "not set"}
          </span>
          . Effective cart key:{" "}
          <span className="font-medium text-slate-900">{status.effectiveSource}</span>
          {status.orgOrderApiKeyConfigured ? " (org fallback available)" : null}.
        </p>
      ) : null}
      {formError ? (
        <p className="text-red-700" role="alert">
          {formError}
        </p>
      ) : null}
      {successNote ? (
        <p className="text-green-800" role="status">
          {successNote}
        </p>
      ) : null}
      <div className="max-w-md space-y-2">
        <Label htmlFor="personal-mouser-order-key">Mouser Order/Cart API key</Label>
        <Input
          id="personal-mouser-order-key"
          type="password"
          value={draftKey}
          onChange={(event) => setDraftKey(event.target.value)}
          autoComplete="off"
          spellCheck={false}
          placeholder={status?.personalOrderApiKeyConfigured ? "Enter new key to replace" : "Paste Manage APIs UUID"}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={saveMutation.isPending || draftKey.trim() === ""}
          onClick={() => saveMutation.mutate(draftKey.trim())}
        >
          {saveMutation.isPending ? "Saving…" : "Save key"}
        </Button>
        {status?.personalOrderApiKeyConfigured ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={removeMutation.isPending}
            onClick={() => removeMutation.mutate()}
          >
            Remove my key
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={testMutation.isPending || status?.effectiveSource === "none"}
          onClick={() => testMutation.mutate()}
        >
          {testMutation.isPending ? "Testing…" : "Test cart access"}
        </Button>
      </div>
      {testMessage ? <p className="text-slate-600">{testMessage}</p> : null}
    </div>
  );
}
